import React, { useState } from 'react';
import { default as ReactModal } from 'react-modal';
import _ from 'lodash';
import cn from 'classnames';
import * as yup from 'yup';
import propertyExprSplit from 'property-expr';
import { Modal } from '../../components/modal/Modal';
import { ModalInput } from '../../components/forms/Input';
import styles from './waiver-airline-relationships-modal.module.css';

const rootElement =
  document.getElementById('ng-app') || document.getElementsByTagName('body')[0];
if (rootElement) {
  // only set this up when we're in the real application, and not in situations like Storybook
  ReactModal.setAppElement(rootElement);
}

const airlineCodeMessage = 'Airline codes must be exactly 2 characters';

export const schema = yup
  .object()
  .shape({
    bookedOn: yup
      .array()
      .transform(function(value, originalValue) {
        if (this.isType(value) && value !== null) {
          return value;
        }
        return originalValue ? originalValue.split(/[\s,]+/) : [];
      })
      .of(
        yup
          .string()
          .trim()
          .length(2, airlineCodeMessage)
          .uppercase(),
      )
      .compact()
      .label('Booked On'),
    operatedBy: yup
      .array()
      .transform(function(value, originalValue) {
        if (this.isType(value) && value !== null) {
          return value;
        }
        return originalValue ? originalValue.split(/[\s,]+/) : [];
      })
      .of(
        yup
          .string()
          .trim()
          .length(2, airlineCodeMessage)
          .uppercase(),
      )
      .compact()
      .label('Operated By'),
  })
  .test({
    name: 'atLeastOneOf',
    test: ({ bookedOn = [], operatedBy = [] }) => {
      return bookedOn.length !== 0 || operatedBy.length !== 0;
    },
    message: 'Either Booked On or Operated By is required.',
  });

interface Relationship {
  bookedOn: string[];
  operatedBy: string[];
};

interface ModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (relationship: Relationship) => void;
};

interface FormValues {
  [key: string]: string;
  bookedOn: string;
  operatedBy: string;
};

const WaiverAirlineRelationshipsModal = ({
  isOpen,
  onCancel,
  onSave,
}: ModalProps) => {
  const [relationship, setRelationship] = useState<FormValues>({
    bookedOn: '',
    operatedBy: '',
  });

  const [errors, setErrors] = useState<Partial<FormValues & { root?: string }>>({
    bookedOn: '',
    operatedBy: '',
  });

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);

  const clear = () => {
    setRelationship({
      bookedOn: '',
      operatedBy: '',
    });
    setErrors({
      bookedOn: '',
      operatedBy: '',
    });
  };

  if (prevIsOpen != isOpen) {
    clear();
    setPrevIsOpen(isOpen);
  }

  const handleSave = async () => {
    try {
      const value = await schema.validate(relationship) as Relationship;
      onSave(value);
    } catch (err) {
      if (!(err instanceof yup.ValidationError)) {
        throw err;
      }

      if (err.path) {
        const pathList = propertyExprSplit.split(err.path);
        setErrors({ [pathList[0]]: err.message });
      } else {
        setErrors({ root: _.first(err.errors) });
      }
    }
  };

  const onChange = (name: string, value: string) => {
    setRelationship({ ...relationship, ...{ [name]: value } });
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpen={() => {}}
      onClose={onCancel}
      onSubmit={() => {
        handleSave();
      }}
      size="medium"
      title="Add Airline Relationships"
    >
      <Form values={relationship} {...{ onChange, errors }} />
    </Modal>
  );
};

interface FormErrors {
  bookedOn?: string;
  operatedBy?: string;
  root?: string;
  [key: string]: string | undefined;
};

interface FormProps {
  onChange: (name: string, value: string) => void;
  values: FormValues;
  errors: FormErrors;
};

const Form = ({ onChange, values, errors }: FormProps) => (
  <div className={cn(styles.form)}>
    <Row
      label="Booked On"
      name="bookedOn"
      placeholder="AA"
      onChange={onChange}
      values={values}
      errors={errors}
      autoFocus
    />
    <Row
      label="Operated By"
      name="operatedBy"
      placeholder="BA, CX, AY"
      onChange={onChange}
      values={values}
      errors={errors}
    />
    <div className={cn('row', styles.row)}>
      <div className="col-md-3"></div>
      <div className="col-md-9">
        <Error message={errors['root']} />
      </div>
    </div>

    <div className={cn('row', styles.row)}>
      <div className="col-md-3"></div>
      <div className="col-md-9">
        <div className="form-note-info">
          Enter single or multiple IATA airline codes separated by commas or
          spaces.
        </div>
      </div>
    </div>
  </div>
);

interface RowProps {
  label: string;
  name: string;
  placeholder: string;
  onChange: (name: string, value: string) => void;
  values: FormValues;
  errors: FormErrors;
  autoFocus?: boolean;
};

const Row = ({
  label,
  name,
  placeholder,
  onChange,
  values,
  errors,
  autoFocus,
}: RowProps) => (
  <div className={cn('row', styles.row)}>
    <label className={cn('col-sm-3', 'fs-label', styles.label)} htmlFor={name}>
      {label}
    </label>

    <div className={cn('col-sm-8')}>
      <ModalInput
        name={name}
        placeholder={placeholder}
        onChange={e => onChange(name, e.currentTarget.value)}
        value={values[name]}
        error={!!errors[name]}
        autoFocus={!!autoFocus}
        id={name}
      />
      <Error message={errors[name]} />
    </div>
  </div>
);

interface ErrorProps {
  message?: string
};

const Error = ({ message }: ErrorProps) => {
  return message ? <span className={cn(styles.error)}>{message}</span> : null;
};

export default WaiverAirlineRelationshipsModal;